import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, ChevronDown } from 'lucide-react';
import { readConsent, saveConsent, acceptAll, rejectAll, applyConsent } from '@/lib/consent';

export default function CookieBanner({ forceOpen, onClose }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, ads: false, personalization: false });

  useEffect(() => {
    const existing = readConsent();
    if (existing) {
      applyConsent(existing);
    }
    if (!existing) {
      // Small delay to avoid layout shift on first paint
      const t2 = setTimeout(() => setVisible(true), 400);
      return () => clearTimeout(t2);
    }
  }, []);

  useEffect(() => {
    if (forceOpen) {
      const existing = readConsent();
      if (existing) {
        setPrefs({
          analytics: !!existing.analytics,
          ads: !!existing.ads,
          personalization: !!existing.personalization,
        });
      }
      setVisible(true);
      setShowPrefs(true);
    }
  }, [forceOpen]);

  const handleAcceptAll = () => {
    acceptAll();
    setVisible(false);
    setShowPrefs(false);
    onClose && onClose();
  };
  const handleRejectAll = () => {
    rejectAll();
    setVisible(false);
    setShowPrefs(false);
    onClose && onClose();
  };
  const handleSave = () => {
    saveConsent({ necessary: true, ...prefs });
    setVisible(false);
    setShowPrefs(false);
    onClose && onClose();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-3 bottom-3 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-[440px] z-50" role="dialog" aria-live="polite" data-testid="cookie-banner">
      <div className="bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(11,30,63,0.35)] border border-[#0B1E3F]/10 overflow-hidden">
        <div className="p-5 md:p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#F6F3EE] flex items-center justify-center shrink-0">
              <Cookie size={18} className="text-[#0B1E3F]" />
            </div>
            <div className="flex-1">
              <h3 className="font-serif text-[19px] font-semibold text-[#0B1E3F] leading-tight">{t('cookie.title')}</h3>
              <p className="mt-2 text-[13px] leading-relaxed text-[#0B1E3F]/70">{t('cookie.body')}</p>
            </div>
          </div>

          {showPrefs && (
            <div className="mt-4 space-y-2">
              <PrefRow title={t('cookie.catNecessaryTitle')} desc={t('cookie.catNecessaryDesc')} checked={true} disabled />
              <PrefRow title={t('cookie.catAnalyticsTitle')} desc={t('cookie.catAnalyticsDesc')} checked={prefs.analytics} onChange={(v) => setPrefs({ ...prefs, analytics: v })} testId="pref-analytics" />
              <PrefRow title={t('cookie.catAdsTitle')} desc={t('cookie.catAdsDesc')} checked={prefs.ads} onChange={(v) => setPrefs({ ...prefs, ads: v })} testId="pref-ads" />
              <PrefRow title={t('cookie.catPersonalizationTitle')} desc={t('cookie.catPersonalizationDesc')} checked={prefs.personalization} onChange={(v) => setPrefs({ ...prefs, personalization: v })} testId="pref-personalization" />
            </div>
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            {!showPrefs && (
              <>
                <button onClick={handleAcceptAll} className="flex-1 min-w-[130px] bg-[#0B1E3F] hover:bg-[#12295a] text-white text-[13px] font-semibold px-4 py-2.5 rounded-full transition" data-testid="cookie-accept">
                  {t('cookie.accept')}
                </button>
                <button onClick={handleRejectAll} className="flex-1 min-w-[130px] bg-[#F6F3EE] hover:bg-[#EAE5DC] text-[#0B1E3F] text-[13px] font-semibold px-4 py-2.5 rounded-full transition" data-testid="cookie-reject">
                  {t('cookie.reject')}
                </button>
                <button onClick={() => setShowPrefs(true)} className="w-full text-[12px] text-[#0B1E3F]/70 hover:text-[#0B1E3F] mt-1 flex items-center justify-center gap-1 underline underline-offset-2" data-testid="cookie-manage">
                  {t('cookie.manage')} <ChevronDown size={12} />
                </button>
              </>
            )}
            {showPrefs && (
              <>
                <button onClick={handleSave} className="flex-1 min-w-[130px] bg-[#0B1E3F] hover:bg-[#12295a] text-white text-[13px] font-semibold px-4 py-2.5 rounded-full transition" data-testid="cookie-save">
                  {t('cookie.save')}
                </button>
                <button onClick={handleAcceptAll} className="flex-1 min-w-[130px] bg-[#E9B949] hover:bg-[#d8ab3e] text-[#0B1E3F] text-[13px] font-semibold px-4 py-2.5 rounded-full transition" data-testid="cookie-accept-2">
                  {t('cookie.accept')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PrefRow({ title, desc, checked, onChange, disabled, testId }) {
  return (
    <label className={`flex items-start gap-3 p-3 rounded-xl border border-[#0B1E3F]/10 ${disabled ? 'bg-[#F6F3EE]/60' : 'hover:bg-[#F6F3EE]/40 cursor-pointer'}`}>
      <div className="flex-1">
        <div className="text-[13px] font-semibold text-[#0B1E3F]">{title}</div>
        <div className="text-[11.5px] text-[#0B1E3F]/60 leading-relaxed mt-0.5">{desc}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        data-testid={testId}
        className={`relative w-10 h-6 rounded-full transition shrink-0 ${checked ? 'bg-[#0B1E3F]' : 'bg-[#0B1E3F]/20'} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <span className={`absolute top-0.5 ${checked ? 'left-[18px]' : 'left-0.5'} w-5 h-5 bg-white rounded-full transition-all shadow`} />
      </button>
    </label>
  );
}
